import React, { useRef, useEffect, useState, useCallback } from "react";
import ModalNewSettingsRRCode from "../components/modalNewSettingsRRCode";
import { useSettings } from "../../settings/settingsContext";
import ModalNewSettingsRRCredentials from "../components/modalNewSettingsRRCredentials";
import ModalNewMap from "../components/modalNewMap";
import ModalUpdateMap from "../components/modalUpdateMap";
import ModalEditPEXConnection from "../components/modalEditPEXConnection";
import ModalEditStarRezConnection from "../components/modalEditStarRezConnection";
import ModalNewStarRezMap from "../components/modalNewStarRezMap";
import ModalUpdateStarRezMap from "../components/modalUpdateStarRezMap";
import { postWithAuth } from "../../api/api";
import { API_URLS } from "../../api/apiConfig";

const YesSettingsPage = ({ settings, pexConnection, starRezConnection }) => {
  const settingsContext = useSettings();

  const [mappings, setMappings] = useState([]);
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const fetchMappings = useCallback(async () => {
    setIsLoadingMappings(true);
    try {
      const url = starRezConnection
        ? API_URLS.LIST_STARREZ_MAPPINGS_BY_FILTERS
        : API_URLS.LIST_PEX_MAPPINGS_BY_FILTERS;

      const response = await postWithAuth(url, {
        page,
        limit,
        search_text: searchText || undefined,
      });

      if (response.message === "success") {
        setMappings(response.data.items);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error("Error fetching mappings:", error);
    } finally {
      setIsLoadingMappings(false);
    }
  }, [page, limit, searchText, starRezConnection]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchText(searchInput);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <div className="card mb-5 mb-xxl-10">
        <div className="card-header border-0 pt-6">
          <div className="card-title">
            <h3>
              {starRezConnection
                ? "Mapeo de propiedad RR-Wofy-StarRez"
                : "Mapeo de propiedad RR-Wofy-PEX"}
            </h3>
          </div>
          <div className="card-toolbar">
            <div
              className="d-flex justify-content-end align-items-center gap-2"
              data-kt-user-table-toolbar="base"
            >
              <form
                onSubmit={handleSearch}
                className="d-flex align-items-center gap-2"
              >
                <input
                  type="text"
                  className="form-control form-control-sm form-control-solid w-200px"
                  placeholder="Buscar..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button type="submit" className="btn btn-sm btn-light-primary">
                  <i className="ki-outline ki-magnifier fs-5"></i>
                </button>
              </form>
              <a
                onClick={() =>
                  starRezConnection
                    ? settingsContext.openNewStarRezMappingModal()
                    : settingsContext.openNewPexMappingModal()
                }
                className="btn btn-sm btn-primary mx-2"
              >
                <i className="icon-in-button-color ki-outline ki-plus"></i>
                Nuevo Mapeo
              </a>
            </div>
          </div>
        </div>
        <div className="card-body py-10">
          {isLoadingMappings ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "150px" }}
            >
              <div
                className="spinner-grow text-color-amarillo-livensa"
                style={{ width: "3rem", height: "3rem" }}
                role="status"
              ></div>
            </div>
          ) : (
            <div className="dataTables_wrapper dt-bootstrap4 no-footer">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
                  <thead>
                    <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                      <th className="min-w-125px">Número Wofy</th>
                      <th className="min-w-125px">Nombre Wofy</th>
                      <th className="min-w-125px">
                        {starRezConnection ? "Número StarRez" : "Número PEX"}
                      </th>
                      <th className="min-w-125px">
                        {starRezConnection ? "Nombre StarRez" : "Nombre PEX"}
                      </th>
                      <th className="min-w-125px">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600 fw-semibold">
                    {mappings.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-6">
                          No hay mapeos configurados
                        </td>
                      </tr>
                    ) : (
                      mappings.map((mapping, index) => (
                        <tr key={index}>
                          <td>{mapping.wofy_number}</td>
                          <td>{mapping.wofy_name}</td>
                          <td>
                            {starRezConnection
                              ? mapping.starrez_number
                              : mapping.pex_number}
                          </td>
                          <td>
                            {starRezConnection
                              ? mapping.starrez_name
                              : mapping.pex_name}
                          </td>
                          <td>
                            {starRezConnection ? (
                              <TableStarRezMapActions mapping={mapping} />
                            ) : (
                              <TableMapActions pexMapping={mapping} />
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="row mt-4">
                <div className="col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start">
                  <div className="dataTables_length">
                    <select
                      className="form-select form-select-sm form-select-solid"
                      style={{ width: "120px" }}
                      value={limit}
                      onChange={(e) => {
                        setPage(1);
                        setLimit(Number(e.target.value));
                      }}
                    >
                      <option value="10">10 por página</option>
                      <option value="25">25 por página</option>
                      <option value="50">50 por página</option>
                    </select>
                  </div>
                </div>
                <div className="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end">
                  <nav>
                    <ul className="pagination mb-0">
                      <li
                        className={`page-item ${page === 1 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setPage(page - 1)}
                        >
                          Anterior
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link">
                          Página {page} de {totalPages || 1}
                        </span>
                      </li>
                      <li
                        className={`page-item ${page >= totalPages ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setPage(page + 1)}
                        >
                          Siguiente
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card mb-5 mb-xxl-10">
        <div className="card-header">
          <div className="card-title">
            <h3>Conexión RR</h3>
          </div>
          <div className="card-toolbar">
            <label className="form-check form-switch form-switch-sm form-check-custom form-check-solid">
              <span className="form-check-label text-muted">
                {process.env.REACT_APP_API_ENVIRONMENT} Enviroment
              </span>
            </label>
          </div>
        </div>
        <div className="card-body py-10">
          <div className="row mb-10">
            <div className="col-md-6 pb-10 pb-lg-0">
              <h2>Usuario, contraseña y endpoint</h2>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Es necesario establecer un usuario y contraseña establecido por
                ambas partes para securizar la información. El endpoint es la
                url donde el proveedor externo debe enviar la información
              </p>
              <p className="text-gray-800 fw-bold mb-1 fs-6 py-2">
                https://api.livensa.com
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Usuario recepción reservas: {settings.username}
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Pass recepción reservas: {settings.password}
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Usuario envío reservas: {settings.username_send || "N/D"}
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Pass envío reservas: {settings.password_send || "N/D"}
              </p>
              <a
                onClick={() =>
                  settingsContext.openNewSettingsRRCredentialsModal(settings)
                }
                className="btn btn-light btn-active-light-primary"
              >
                Cambiar user y pass
              </a>
            </div>
            <div className="col-md-6">
              <h2>Código de propiedad</h2>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                El código de la propiedad, conocido por ambas partes, reconoce a
                la propiedad con respecto al resto de propiedades
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Código: {settings.hotel_code}
              </p>
              <a
                onClick={() =>
                  settingsContext.openNewSettingsRRCodeModal(settings)
                }
                className="btn btn-light btn-active-light-primary"
              >
                Cambiar código
              </a>
            </div>
          </div>
          <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed  p-6">
            <i className="ki-outline ki-design-1 fs-2tx text-primary me-4"></i>
            <div className="d-flex flex-stack flex-grow-1 ">
              <div className=" fw-semibold">
                <div className="fs-6 text-gray-700 ">
                  {" "}
                  Cambiar estos valores implica una posible perdida de conexión
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-5 mb-xxl-10">
        <div className="card-header">
          <div className="card-title">
            <h3>Conexión PEX</h3>
          </div>
          <div className="card-toolbar">
            <label className="form-check form-switch form-switch-sm form-check-custom form-check-solid">
              <span className="form-check-label text-muted">
                {process.env.REACT_APP_API_ENVIRONMENT} Enviroment
              </span>
            </label>
          </div>
        </div>
        <div className="card-body py-10">
          <div className="row mb-10">
            <div className="col-md-6 pb-10 pb-lg-0">
              <h2>Mapeo de Building</h2>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Es necesario establecer "building name" para mapear la reserva
                de PEX con la propiedad en Wofy House Keeping
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Building:{" "}
                {pexConnection ? pexConnection.building_name : "No disponible"}
              </p>
              <a
                onClick={() =>
                  settingsContext.openEditPEXConnectionModal(pexConnection)
                }
                className="btn btn-light btn-active-light-primary"
              >
                Cambiar "building name"
              </a>
            </div>
          </div>
          <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed  p-6">
            <i className="ki-outline ki-design-1 fs-2tx text-primary me-4"></i>
            <div className="d-flex flex-stack flex-grow-1 ">
              <div className=" fw-semibold">
                <div className="fs-6 text-gray-700 ">
                  {" "}
                  Cambiar estos valores implica una posible perdida de conexión
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-5 mb-xxl-10">
        <div className="card-header">
          <div className="card-title">
            <h3>Conexión StarRez</h3>
          </div>
          <div className="card-toolbar">
            <label className="form-check form-switch form-switch-sm form-check-custom form-check-solid">
              <span className="form-check-label text-muted">
                {process.env.REACT_APP_API_ENVIRONMENT} Enviroment
              </span>
            </label>
          </div>
        </div>
        <div className="card-body py-10">
          <div className="row mb-10">
            <div className="col-md-6 pb-10 pb-lg-0">
              <h2>Mapeo de Building</h2>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Es necesario establecer "building name" para mapear la reserva
                de StarRez con la propiedad en Wofy House Keeping
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Building:{" "}
                {starRezConnection
                  ? starRezConnection.building_name
                  : "No disponible"}
              </p>
              <p className="fs-6 fw-semibold text-gray-600 py-2">
                Location ID:{" "}
                {starRezConnection
                  ? starRezConnection.room_location_id
                  : "No disponible"}
              </p>
              <a
                onClick={() =>
                  settingsContext.openEditStarRezConnectionModal(
                    starRezConnection,
                  )
                }
                className="btn btn-light btn-active-light-primary"
              >
                Cambiar "building name"
              </a>
            </div>
          </div>
          <div className="notice d-flex bg-light-primary rounded border-primary border border-dashed  p-6">
            <i className="ki-outline ki-design-1 fs-2tx text-primary me-4"></i>
            <div className="d-flex flex-stack flex-grow-1 ">
              <div className=" fw-semibold">
                <div className="fs-6 text-gray-700 ">
                  {" "}
                  Cambiar estos valores implica una posible perdida de conexión
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {settingsContext.showEditPEXConnectionModal && (
        <ModalEditPEXConnection
          onClose={() => settingsContext.closeEditPEXConnectionModal()}
          pexConnection={settingsContext.pexConnection}
        />
      )}

      {settingsContext.showEditStarRezConnectionModal && (
        <ModalEditStarRezConnection
          onClose={() => settingsContext.closeEditStarRezConnectionModal()}
          starRezConnection={settingsContext.starRezConnection}
        />
      )}

      {settingsContext.showNewPexMappingModal && (
        <ModalNewMap
          onClose={() => settingsContext.closeNewPexMappingModal()}
        />
      )}

      {settingsContext.showEditPexMappingModal && (
        <ModalUpdateMap
          onClose={() => settingsContext.closeEditPexMappingModal()}
          pexMapping={settingsContext.pexMapping}
        />
      )}

      {settingsContext.showNewStarRezMappingModal && (
        <ModalNewStarRezMap
          onClose={() => settingsContext.closeNewStarRezMappingModal()}
        />
      )}

      {settingsContext.showEditStarRezMappingModal && (
        <ModalUpdateStarRezMap
          onClose={() => settingsContext.closeEditStarRezMappingModal()}
          starRezMapping={settingsContext.starRezMapping}
        />
      )}

      {settingsContext.showNewSettingsRRCodeModal && (
        <ModalNewSettingsRRCode
          onClose={() => settingsContext.closeNewSettingsRRCodeModal()}
          settings={settingsContext.settings}
        />
      )}

      {settingsContext.showNewSettingsRRCredentialsModal && (
        <ModalNewSettingsRRCredentials
          onClose={() => settingsContext.closeNewSettingsRRCredentialsModal()}
          settings={settingsContext.settings}
        />
      )}
    </>
  );
};

export default YesSettingsPage;

const TableStarRezMapActions = ({ mapping }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef(null);
  const anchorRef = useRef(null);
  const settingsContext = useSettings();

  const toggleDropdown = () => setShowMenu(!showMenu);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    if (showMenu) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const elementWidth = anchorRef.current.offsetWidth;
      const screenWidth = window.innerWidth;
      const menuX = anchorRect.left - screenWidth + elementWidth;
      const menuY = anchorRect.top + anchorRect.height + 10;
      setMenuPosition({ x: menuX, y: menuY });
    }
  }, [showMenu]);

  return (
    <>
      <a
        href="#"
        className="btn btn-sm btn-light btn-flex btn-center btn-active-light-primary show menu-dropdown"
        onClick={toggleDropdown}
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-end"
        ref={anchorRef}
      >
        Acciones
        <i className="ki-outline ki-down text-color-azul-livensa fs-5 ms-1"></i>
      </a>
      <div
        ref={menuRef}
        style={{
          transform: `translate3d(${menuPosition.x}px, ${menuPosition.y}px, 0)`,
        }}
        className={`menu-actions-table menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-semibold fs-7 w-125px py-4 ${showMenu ? "show" : ""}`}
        data-kt-menu="true"
        data-popper-placement="bottom-end"
      >
        <div className="menu-item px-3">
          <a
            onClick={() => settingsContext.openEditStarRezMappingModal(mapping)}
            className="menu-link px-3"
          >
            Editar
          </a>
        </div>
      </div>
    </>
  );
};

const TableMapActions = ({ pexMapping }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef(null);
  const anchorRef = useRef(null);
  const settingsContext = useSettings();

  const toggleDropdown = () => {
    setShowMenu(!showMenu);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  useEffect(() => {
    if (showMenu) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const elementWidth = anchorRef.current.offsetWidth;
      const screenWidth = window.innerWidth;
      const menuX = anchorRect.left - screenWidth + elementWidth;
      const menuY = anchorRect.top + anchorRect.height + 10;
      setMenuPosition({ x: menuX, y: menuY });
    }
  }, [showMenu]);

  return (
    <>
      <a
        href="#"
        className="btn btn-sm btn-light btn-flex btn-center btn-active-light-primary show menu-dropdown"
        onClick={toggleDropdown}
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-end"
        ref={anchorRef}
      >
        Acciones
        <i className="ki-outline ki-down text-color-azul-livensa fs-5 ms-1"></i>
      </a>
      <div
        ref={menuRef}
        style={{
          transform: `translate3d(${menuPosition.x}px, ${menuPosition.y}px, 0)`,
        }}
        className={`menu-actions-table menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-semibold fs-7 w-125px py-4 ${showMenu ? "show" : ""}`}
        data-kt-menu="true"
        data-popper-placement="bottom-end"
      >
        <div className="menu-item px-3">
          <a
            onClick={() => settingsContext.openEditPexMappingModal(pexMapping)}
            className="menu-link px-3"
          >
            Editar
          </a>
        </div>
      </div>
    </>
  );
};
